import React, { useState, useEffect } from 'react';
import { optimizeImage } from '../lib/cms';

export default function GalleryLightbox({ gallery, className = '' }) {
  const [activeIdx, setActiveIdx] = useState(null);

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const images = gallery.images;

  // Keyboard navigation
  useEffect(() => {
    if (activeIdx === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveIdx(null);
      if (e.key === 'ArrowRight') setActiveIdx((prev) => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setActiveIdx((prev) => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIdx, images.length]);

  return (
    <div className={`rounded-md border border-slate-200 bg-white p-4 sm:p-6 shadow-xs ${className}`}>
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302]">
            Fotogalleri
          </span>
          <h4 className="font-serif text-lg font-bold text-[#010b27]">
            {gallery.title}
          </h4>
        </div>
        <span className="text-xs text-slate-500 font-mono">
          {images.length} fotos
        </span>
      </div>

      {gallery.description && (
        <p className="mt-2 text-xs sm:text-sm text-slate-600 italic">
          {gallery.description}
        </p>
      )}

      {/* Grid of photos */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((imgUrl, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActiveIdx(i)}
            className="group relative aspect-4/3 overflow-hidden rounded-sm border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#fd7302]"
            aria-label={`Åbn billede ${i + 1} af ${images.length}`}
          >
            <img
              src={optimizeImage(imgUrl, { width: 500 })}
              alt={`${gallery.title} foto ${i + 1}`}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#010b27]/80 text-white text-xs px-2 py-1 rounded-sm">
                <i className="fa-solid fa-magnifying-glass-plus mr-1 text-[#fd7302]" aria-hidden="true" />
                Forstør
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {activeIdx !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-xs"
          onClick={() => setActiveIdx(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-md bg-[#010b27] p-2 sm:p-4 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar with close button */}
            <div className="flex items-center justify-between pb-3 text-xs text-slate-400 border-b border-slate-800">
              <span className="font-serif text-sm font-semibold text-white">
                {gallery.title} ({activeIdx + 1} / {images.length})
              </span>
              <button
                type="button"
                onClick={() => setActiveIdx(null)}
                className="rounded-sm p-2 text-slate-400 hover:text-white focus:outline-none"
                aria-label="Luk galleri"
              >
                <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
              </button>
            </div>

            {/* Main image */}
            <div className="relative mt-3 flex items-center justify-center min-h-[300px] max-h-[70vh]">
              <img
                src={optimizeImage(images[activeIdx], { width: 1400 })}
                alt={`${gallery.title} forstørret visning`}
                className="max-h-[68vh] w-auto max-w-full object-contain"
              />

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveIdx((prev) => (prev - 1 + images.length) % images.length);
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white transition-colors hover:bg-[#fd7302] focus:outline-none"
                    aria-label="Forrige billede"
                  >
                    <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveIdx((prev) => (prev + 1) % images.length);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white transition-colors hover:bg-[#fd7302] focus:outline-none"
                    aria-label="Næste billede"
                  >
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </button>
                </>
              )}
            </div>

            {/* Bottom thumbnail strip */}
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 pt-2 border-t border-slate-800">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  className={`h-12 w-16 shrink-0 overflow-hidden rounded-sm border-2 transition-all ${
                    idx === activeIdx ? 'border-[#fd7302] scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={optimizeImage(img, { width: 120 })}
                    alt={`Miniature ${idx + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
